import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Search, ListPlus, ListStart, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { useStudioApi, errMsg, fmtTime } from "@/lib/api";
import { EditTrackDialog } from "@/components/studio/EditTrackDialog";

export const LibraryPanel = ({ act, onChange }) => {
  const call = useStudioApi();
  const [tracks, setTracks] = useState([]);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const input = useRef(null);

  const load = useCallback(() => call("get", `/studio/tracks?q=${encodeURIComponent(q)}`).then(setTracks).catch((e) => toast.error(errMsg(e))), [call, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const upload = async (files) => {
    if (!files?.length) return;
    const form = new FormData();
    [...files].forEach((f) => form.append("files", f));
    setUploading(true);
    try {
      const res = await call("post", "/studio/tracks", form);
      if (res.created.length) toast.success(`${res.created.length} track(s) added to the library`);
      res.errors.forEach((e) => toast.error(`${e.file}: ${e.error}`));
      load(); onChange();
    } catch (e) { toast.error(errMsg(e)); }
    setUploading(false);
    input.current.value = "";
  };

  const remove = async (t) => {
    if (!window.confirm(`Remove "${t.title}" from the library?`)) return;
    try { await call("delete", `/studio/tracks/${t.id}`); toast.success("Track removed"); load(); onChange(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section className="panel p-5 mt-3" data-testid="studio-library-panel">
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--lr-muted)]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, artist, album" className="pl-9 bg-black/30" data-testid="studio-library-search-input" />
        </div>
        <input ref={input} type="file" multiple accept=".mp3,.aac,.m4a,.ogg,.wav,.flac,audio/*" className="hidden" onChange={(e) => upload(e.target.files)} data-testid="studio-upload-input" />
        <button onClick={() => input.current.click()} disabled={uploading} className="rounded-lg bg-[color:var(--red)] px-5 py-2.5 font-bold flex items-center justify-center gap-2 hover:brightness-110 disabled:opacity-60 transition-[filter]" data-testid="studio-upload-button">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {uploading ? "Uploading..." : "Upload music"}
        </button>
      </div>
      {!tracks.length && <p className="py-8 text-center text-sm text-[color:var(--lr-muted)]" data-testid="studio-library-empty">No tracks yet. Upload MP3, AAC, OGG, WAV or FLAC files to get the rotation going.</p>}
      <div className="divide-y divide-white/5" data-testid="studio-library-list">
        {tracks.map((t, i) => (
          <div key={t.id} className="flex items-center gap-3 py-2.5" data-testid={`studio-library-item-${i}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-sm">{t.title}</p>
              <p className="truncate text-xs text-[color:var(--lr-muted)]">{[t.artist || "Unknown artist", t.album].filter(Boolean).join(" / ")}</p>
            </div>
            <span className="font-mono text-xs text-[color:var(--gold)] hidden sm:block w-16 text-right" data-testid={`studio-library-bpm-${i}`}>{t.bpm ? `${Math.round(t.bpm)} BPM` : "--"}</span>
            <span className="font-mono text-xs text-[color:var(--lr-muted)] hidden sm:block">{fmtTime(t.duration)}</span>
            <button onClick={() => act("post", "/studio/queue", { track_id: t.id, position: "next" })} title="Play next" className="p-2 rounded-md hover:bg-white/10 text-[color:var(--gold)]" data-testid={`studio-library-play-next-${i}`}><ListStart className="w-4 h-4" /></button>
            <button onClick={() => act("post", "/studio/queue", { track_id: t.id, position: "end" })} title="Add to queue" className="p-2 rounded-md hover:bg-white/10 text-[color:var(--cyan)]" data-testid={`studio-library-add-queue-${i}`}><ListPlus className="w-4 h-4" /></button>
            <button onClick={() => setEditing(t)} title="Edit" className="p-2 rounded-md hover:bg-white/10" data-testid={`studio-library-edit-${i}`}><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(t)} title="Remove" className="p-2 rounded-md hover:bg-white/10 text-[color:var(--red)]" data-testid={`studio-library-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <EditTrackDialog track={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); onChange(); }} />
    </section>
  );
};
